import {
  Elements,
  PaymentMethodMessagingElement,
} from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import { BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import {
  getProductByIdNotClean,
  getProductViews,
} from '@util/firestore/products';
import { getReviewsByUid } from '@util/firestore/reviews';
import {
  getCheapestEstimate,
  getEstimates,
  isDomestic,
} from '@util/firestore/shipengine';
import { getStripeAmount } from '@util/get-stripe';
import { formatRange, getCountryLabelFromCode } from '@util/index';
import ProductPolicyAccordion from 'app/product/[slug]/components/ProductPolicyAccordion';
import { useAuth } from 'context/AuthContext';
import { useStripeContext } from 'context/StripeContext';
import dayjs from 'dayjs';
import { FlagIcon } from 'lucide-react';
import { ProductDocument, Variation } from 'models/product';
import { PublicUserDocument } from 'models/user';
import { useEffect, useState } from 'react';
import { Truck } from './cards/MXListProductCard';
import { EyeIcon } from './icons';
import MXShoppingBag from './icons/mxShoppingBag';
import ReportItemModal from './modals/ReportItemModal';
import { MXReturnIcon } from './MXReturnOption';
import ActionButtonsSkeleton from './product-details/ActionButtonsSkeleton';
import AuctionActions from './product-details/AuctionActions';
import BuyerActionButtons from './product-details/BuyerActionButtons';
import ProductDetailsImages from './product-details/ProductDetailsImages';
import ProductPriceAndShipping from './product-details/ProductPriceAndShipping';
import ProductVariantSelector from './product-details/ProductVariantSelector';
import SellerActionButtons from './product-details/SellerActionButtons';
import SoldOrDraftBlock from './product-details/SoldOrDraftBlock';
import ProductConditions from './ProductConditions';
import ProductSellerBadge from './ProductSellerBadge';
import { getDefaultAddress } from '@util/firestore/users';
interface ProductDetailPageProps {
  product: ProductDocument;
  seller: PublicUserDocument | null;
  breadcrumbs: BreadcrumbItem[];
  isPreview?: boolean;
  schemaRatings: {
    product_rating: string;
    product_reviews: string;
  };
  fitment: Map<string, Map<string, { years: number[]; rideType: string }>>;
  selectedVariations: Variation[] | null;
  onSelection: (v: Variation, isNumber: boolean, isColor: boolean) => void;
  setErrors: React.Dispatch<React.SetStateAction<string[]>>;
}
export const ProductDetails = ({
  product: initialProduct,
  isPreview,
  seller,
  selectedVariations,
  onSelection,
  setErrors,
}: ProductDetailPageProps) => {
  const { userDoc, user } = useAuth();
  const [customInputsEnabled, setCustomInputsEnabled] = useState(false);
  const [reportProductModalOpen, setReportProductModalOpen] = useState(false);
  const [hasCalculatedShipping, setHasCalculatedShipping] = useState(false);

  const { data: product = initialProduct } = useQuery({
    queryKey: ['product', initialProduct.id],
    queryFn: async () => {
      const product = await getProductByIdNotClean(initialProduct.id);
      if (product) {
        if (
          product.category === 'Bike Parts' &&
          !product.fitting_bikes?.bikes.length &&
          !product.disclaimer
        ) {
          product.disclaimer =
            'Please check the description and contact the seller for vehicle fitment compatibility.';
        }
        return product;
      }
      return initialProduct;
    },
    enabled:
      !!userDoc?.uid &&
      (initialProduct.seller_id === userDoc?.uid || userDoc?.roles?.admin),
  });

  const { stripePromise } = useStripeContext();

  const { data: shipping } = useQuery({
    queryKey: ['shipping', product.shipment, userDoc?.addresses.length],
    queryFn: async () => {
      const getShipping = async () => {
        const isLoggedInAndHasAddress = !!userDoc && userDoc.addresses.length;
        // Not logged in or no address
        if (!isLoggedInAndHasAddress) {
          return product.is_flat_rate
            ? product.shipping_costs?.find((s) => s.code === 'US') ??
                product.shipping_costs?.[0] ??
                null
            : null;
        }

        const address = getDefaultAddress(userDoc);
        if (!address) return null;
        // Flat rate shipping
        if (product.is_flat_rate && product.shipping_costs?.length) {
          const foundShipping = product.shipping_costs.find(
            (s) => s.code === address.country_code
          );
          if (foundShipping) return foundShipping;
          const foundOther = product.shipping_costs.find(
            (s) => s.code === 'OT'
          );
          if (foundOther) return foundOther;
          return null;
        }

        // Domestic flat rate shipping
        if (isDomestic(product.shipment, address)) {
          return {
            cost: product.shipment.domestic_flat_rate,
            name: 'Continental US',
            code: 'US',
            estimated: true,
          };
        }

        // Calculated shipping
        if (address.country_code && address.postal_code) {
          const cheapestEstimate = await getCheapestEstimate(
            product.id,
            address.postal_code,
            address.country_code
          );
          return cheapestEstimate
            ? {
                cost: cheapestEstimate,
                name: address.postal_code,
                code: address.country_code,
              }
            : null;
        }

        return null;
      };
      return getShipping();
    },
  });

  const { data: deliveryDates } = useQuery({
    queryKey: ['deliveryDates', product.id, userDoc?.addresses.length],
    queryFn: async () => {
      let shipTo = userDoc?.addresses?.find((a) => a.is_default);
      if (!shipTo) shipTo = userDoc?.addresses?.[0];

      if (shipTo) {
        const productWithShipment = await getProductByIdNotClean(product.id);

        if (!productWithShipment?.shipment) {
          return {
            earliest: dayjs().add(4, 'days').toISOString(),
            latest: dayjs().add(9, 'days').toISOString(),
          };
        }

        const rates = await getEstimates({
          product_id: product.id,
          to_country_code: shipTo.country_code,
          to_postal_code: shipTo.postal_code ?? '',
        });

        if (rates.length === 0) {
          return {
            earliest: dayjs().add(4, 'days').toISOString(),
            latest: dayjs().add(9, 'days').toISOString(),
          };
        }
        const earliest = rates.reduce((prev, curr) => {
          if (curr.estimated_delivery_date < prev.estimated_delivery_date)
            return curr;
          return prev;
        });
        const latest = rates.reduce((prev, curr) => {
          if (curr.estimated_delivery_date > prev.estimated_delivery_date)
            return curr;
          return prev;
        });

        return {
          earliest: earliest.estimated_delivery_date,
          latest: latest.estimated_delivery_date,
        };
      } else {
        return {
          earliest: dayjs().add(4, 'days').toISOString(),
          latest: dayjs().add(9, 'days').toISOString(),
        };
      }
    },
    enabled: hasCalculatedShipping,
  });

  const { data: reviews } = useQuery({
    queryKey: ['reviews'],
    queryFn: () =>
      getReviewsByUid({
        uid: product.seller_id ?? '',
        reviewsPerPage: 10,
      }),
    enabled: !!product.seller_id,
  });

  const { data: views } = useQuery({
    queryKey: ['views', product.id],
    queryFn: () => getProductViews(product.id),
    enabled: !!product.id,
  });

  const onCustomInputsChange = (enabled: boolean) => {
    setCustomInputsEnabled(enabled);
  };

  // Calculate default delivery dates (3-10 business days for US users)
  const getDefaultDeliveryDates = () => {
    const addBusinessDays = (date: dayjs.Dayjs, days: number) => {
      let result = date;
      let addedDays = 0;
      while (addedDays < days) {
        result = result.add(1, 'day');
        if (result.day() !== 0 && result.day() !== 6) {
          // Skip weekends
          addedDays++;
        }
      }
      return result;
    };

    const today = dayjs();
    return {
      earliest: addBusinessDays(today, 3).toISOString(),
      latest: addBusinessDays(today, 10).toISOString(),
    };
  };

  // Auto-calculate shipping when user is logged in
  useEffect(() => {
    if (userDoc && !hasCalculatedShipping) {
      setHasCalculatedShipping(true);
    }
  }, [userDoc, hasCalculatedShipping]);

  let shippingDates = hasCalculatedShipping
    ? deliveryDates
    : getDefaultDeliveryDates();

  if (product.is_flat_rate && !hasCalculatedShipping) {
    const earliest =
      product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12' ? 2 : 5;
    const latest =
      product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12' ? 3 : 10;
    shippingDates = {
      earliest: dayjs().add(earliest, 'days').toISOString(),
      latest: dayjs().add(latest, 'days').toISOString(),
    };
  }

  return (
    <>
      <div className="flex w-full flex-col gap-y-8 sm:w-auto">
        <div
          className={`flex w-full max-w-default flex-col justify-center gap-[3.2rem] sm:gap-[4.8rem] lg:flex-row `}
        >
          <div>
            <ProductDetailsImages
              product={product}
              selectedVariations={selectedVariations}
            />
          </div>

          <div className="flex w-full max-w-[60rem] flex-col gap-[1.6rem] px-8 sm:gap-[1.6rem] sm:px-4 lg:pt-[.5rem]">
            <div className="flex w-full items-center justify-between gap-x-8">
              <h1 className="text-[2.5rem] font-bold leading-[3.25rem] text-black sm:w-[50rem] sm:text-[2.5rem]">
                {product.title}
              </h1>

              <div className="flex items-center gap-x-2 sm:hidden">
                <EyeIcon className="h-10 w-10 text-[#7F7F7F]" />

                <span className={'font-semibold text-zinc-800'}>
                  {views?.views ?? 0}
                </span>
              </div>
            </div>

            {seller?.uid && product.id && (
              <ProductSellerBadge
                sellerDoc={seller}
                product={product}
                reviews={reviews ?? []}
                isLoading={false}
              />
            )}

            <div className="mt-8 flex flex-col items-start gap-[1.6rem] xl:flex-row">
              <ProductPriceAndShipping
                product={product}
                selectedVariations={selectedVariations}
                shipping={shipping}
                customInputsEnabled={customInputsEnabled}
              />
            </div>

            {!product.out_of_stock &&
              !product.is_draft &&
              !product.is_auction &&
              [
                'US',
                'CA',
                'AU',
                'NZ',
                'GB',
                'IE',
                'FR',
                'ES',
                'DE',
                'AT',
                'BE',
                'DK',
                'FI',
                'IT',
                'NL',
                'NO',
                'SE',
              ].includes(userDoc?.country.code ?? 'US') && (
                <Elements stripe={stripePromise}>
                  <PaymentMethodMessagingElement
                    options={{
                      amount: getStripeAmount(
                        product.price + (shipping?.cost ?? 0)
                      ),
                      currency: 'USD',
                      paymentMethodTypes: ['affirm'],
                      countryCode: (userDoc?.country.code ?? 'US') as
                        | 'AT'
                        | 'AU'
                        | 'BE'
                        | 'CA'
                        | 'DE'
                        | 'DK'
                        | 'ES'
                        | 'FI'
                        | 'FR'
                        | 'GB'
                        | 'IE'
                        | 'IT'
                        | 'NL'
                        | 'NO'
                        | 'NZ'
                        | 'SE'
                        | 'US',
                    }}
                  />
                </Elements>
              )}

            <ProductConditions product={product} />

            {product.has_variations && (
              <ProductVariantSelector
                product={product}
                selectedVariations={selectedVariations ?? []}
                onSelection={onSelection}
              />
            )}

            <div
              className={`${
                isPreview
                  ? 'pointer-events-none relative cursor-not-allowed'
                  : ''
              } contain-layout`}
            >
              {(product.out_of_stock || !product.date_added) &&
              (!product.is_auction || !product.date_added) ? (
                <>
                  <SoldOrDraftBlock
                    variant={
                      product.is_draft && product.seller_id === userDoc?.uid
                        ? 'draft'
                        : 'sold'
                    }
                  />
                  {(!!userDoc?.roles?.reseller || product.has_variations) &&
                    (product.seller_id === userDoc?.uid ||
                      userDoc?.roles?.admin) && (
                      <Button
                        width="fluid"
                        type="tertiary"
                        text="Edit Listing"
                        href={`/edit-listing/${product.id}`}
                      />
                    )}
                </>
              ) : user === undefined || seller === undefined ? (
                <ActionButtonsSkeleton />
              ) : user?.uid === product.seller_id && !isPreview ? (
                <SellerActionButtons product={product} />
              ) : (
                (!product.is_draft || isPreview) && (
                  <BuyerActionButtons
                    product={product}
                    seller={seller}
                    selectedVariations={selectedVariations}
                    setErrors={setErrors}
                    onCustomInputsChange={onCustomInputsChange}
                    hideOffer={
                      !!product.custom_inputs ||
                      product.category === 'Giveaways' ||
                      seller?.offers_disabled
                    }
                  />
                )
              )}

              {!product.is_draft && product.is_auction && (
                <Elements stripe={stripePromise}>
                  <AuctionActions product={product} />
                </Elements>
              )}
            </div>

            {product.disclaimer && (
              <div className="flex items-start gap-x-4 rounded-[2rem] bg-[#F9E8E8] p-[2rem]">
                <DisclaimerIcon className="h-[2.3rem] w-[2.3rem] text-[#7F7F7F]" />

                <span className="text-[1.8rem] text-brand-black">
                  {product.disclaimer}
                </span>
              </div>
            )}

            <ProductPolicyAccordion
              title={'Shipping & Delivery'}
              icon={<Truck className="h-[4rem] w-[4rem]" />}
            >
              <div className="flex w-full flex-col gap-y-2">
                {userDoc?.country?.code === 'US' && (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-[1.8rem] font-semibold text-black">
                        Delivery
                      </span>

                      {product.shipment?.ship_from.country_code &&
                      getCountryLabelFromCode(
                        product.shipment.ship_from.country_code
                      ) !== 'Unknown' ? (
                        <div className="flex w-full items-center justify-between">
                          <div className="flex items-center gap-x-2">
                            <div className="text-[1.6rem] text-brand-gray">
                              Shipping:
                            </div>
                            <p className="text-[1.6rem] text-brand-gray">
                              Ships from{' '}
                              {product.shipment?.ship_from.state_province},{' '}
                              {getCountryLabelFromCode(
                                product.shipment?.ship_from.country_code ?? ''
                              )}
                              .
                            </p>
                          </div>
                        </div>
                      ) : (
                        userDoc?.country?.code &&
                        getCountryLabelFromCode(
                          userDoc?.country?.code ?? ''
                        ) !== 'Unknown' && (
                          <div className="flex w-full items-center justify-between">
                            <div className="flex items-center gap-x-2">
                              <div className="text-[1.6rem] text-brand-gray">
                                Shipping:
                              </div>
                              <p className="text-[1.6rem] text-brand-gray">
                                Ships from{' '}
                                {getCountryLabelFromCode(
                                  userDoc?.country?.code ?? ''
                                )}
                                .
                              </p>
                            </div>
                          </div>
                        )
                      )}

                      <p className="text-[1.6rem] text-brand-gray">
                        Estimated delivery{' '}
                        <span className="font-semibold text-black">
                          {formatRange(shippingDates)}
                        </span>{' '}
                        if you order today!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </ProductPolicyAccordion>

            <ProductPolicyAccordion
              title={'Return Policy'}
              icon={<MXReturnIcon className="h-[3rem] w-[3rem]" />}
            >
              <div className="flex flex-col gap-y-2">
                <span className="text-[1.8rem] font-semibold text-black">
                  Make Any Order Returnable
                </span>

                <p className="text-[1.6rem] text-brand-gray">
                  Want extra peace of mind? Even if a seller doesn&apos;t offer
                  returns, <br />
                  MX Locker gives you the option to make any item returnable
                  with{' '}
                  <span className="font-semibold italic text-zinc-700">
                    Return Assurance
                  </span>{' '}
                  at checkout.
                </p>
              </div>
            </ProductPolicyAccordion>

            <ProductPolicyAccordion
              title={'Shop With Confidence'}
              icon={<MXShoppingBag className="h-[3.5rem] w-[3.5rem]" />}
            >
              <div className="flex flex-col gap-y-6">
                <div className="flex flex-col gap-y-2">
                  <span className="text-[1.8rem] font-semibold text-black">
                    MX Locker Buyer Protection Guaranteed
                  </span>

                  <p className="text-[1.6rem] text-brand-gray">
                    MX Locker is 100% committed to ensuring that every sale ends
                    in satisfaction—for both buyer and seller. Your payment is
                    held until the item is delivered and approved. If it&apos;s
                    not as described, you&apos;ll receive a full refund.
                  </p>
                </div>

                <div className="h-px w-full bg-zinc-300" />

                <div className="flex flex-col gap-y-2">
                  <span className="text-[1.8rem] font-semibold text-black">
                    Secure Payment
                  </span>

                  <p className="text-[1.6rem] text-brand-gray">
                    Every transaction is backed by our secure payment system. We
                    hold funds until you confirm the item arrived in the
                    promised condition—so you can shop worry-free.
                  </p>
                </div>
              </div>
            </ProductPolicyAccordion>

            <div className="flex w-full justify-end">
              <Button
                text="Report this item"
                type="text"
                width="small"
                className="!text-[1.6rem] !font-semibold"
                onClick={() => {
                  setReportProductModalOpen(true);
                }}
                leadingIcon={
                  <FlagIcon
                    fill="white"
                    stroke="#C41719"
                    strokeWidth={2}
                    width={14}
                    height={14}
                  />
                }
              />

              <ReportItemModal
                isOpen={reportProductModalOpen}
                itemId={product.id}
                isUser={false}
                dismiss={() => setReportProductModalOpen(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const DisclaimerIcon = ({ className }: { className?: string }) => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9.375 0C4.20563 0 0 4.20563 0 9.375C0 14.5444 4.20563 18.75 9.375 18.75C14.5444 18.75 18.75 14.5444 18.75 9.375C18.75 4.20563 14.5444 0 9.375 0ZM9.375 3.84375C9.61605 3.84375 9.85168 3.91523 10.0521 4.04915C10.2525 4.18306 10.4087 4.37341 10.501 4.5961C10.5932 4.8188 10.6174 5.06385 10.5703 5.30027C10.5233 5.53668 10.4072 5.75384 10.2368 5.92429C10.0663 6.09473 9.84918 6.21081 9.61277 6.25783C9.37635 6.30486 9.1313 6.28072 8.9086 6.18848C8.68591 6.09623 8.49556 5.94002 8.36165 5.7396C8.22773 5.53918 8.15625 5.30355 8.15625 5.0625C8.15625 4.73927 8.28465 4.42927 8.51321 4.20071C8.74177 3.97215 9.05177 3.84375 9.375 3.84375ZM12.375 14.4375H6.75V12.9375H8.8125V8.8125H7.3125V7.3125H10.3125V12.9375H12.375V14.4375Z"
      fill="#C41719"
    />
  </svg>
);
